// Every string EventChipRow can render, and the shape a caller may replace them
// with.
//
// The mechanism is BidCard's exactly — one object of finished text in one
// language, merged over these defaults key by key, with an UNKNOWN key rejected
// because that is the failure that renders English forever with nothing saying
// why. The reasoning is written out once, in bidCardTranslations.js.
//
// The row's only words of its own are the two a screen reader hears and nobody
// sees: the row's name, and the word that stands in for the live dot. Each
// chip's label is the page's, already in the reader's language, and its count is
// a figure.
//
// Deliberately NOT exported from the package barrel: the catalog gate holds that
// a barrel exports components and nothing else.

import { unreadTranslationKeys as unreadKeysAgainst } from './bidCardTranslations';

/**
 * The English defaults, frozen.
 */
export const EVENT_CHIP_ROW_TRANSLATIONS = Object.freeze({
  // The row's accessible name: what the chips in it choose between.
  label: 'Events',

  // Announced before a live event's label, where the dot is drawn, so the one
  // thing the dot says is said to a reader who cannot see it.
  live: 'Live',
});

/**
 * The keys in `translations` that EventChipRow would not read, as dotted paths.
 *
 * The walk itself is BidCard's, which takes the defaults object as an argument,
 * so this surface passes its own rather than owning a second copy of a
 * comparison that has to behave identically everywhere.
 */
export const unreadTranslationKeys = (translations) =>
  unreadKeysAgainst(translations, EVENT_CHIP_ROW_TRANSLATIONS);

/** One language's text over the English defaults, key by key. */
export function mergeTranslations(translations) {
  if (!translations) {
    return EVENT_CHIP_ROW_TRANSLATIONS;
  }

  return { ...EVENT_CHIP_ROW_TRANSLATIONS, ...translations };
}

/**
 * The `i18n` prop's validator: `{ translations }`, optional.
 *
 * Closed on the outer object too — `translation` for `translations` fails
 * exactly the way a mis-keyed string does, silently and in English.
 */
export function isValidEventChipRowI18n(i18n) {
  if (i18n === null || typeof i18n !== 'object') {
    return false;
  }
  if (Object.keys(i18n).some((key) => key !== 'translations')) {
    return false;
  }
  if (i18n.translations === undefined) {
    return true;
  }
  return unreadTranslationKeys(i18n.translations).length === 0;
}
